// © 2026 AttainIP Engineering
/**
 * Geo-detection for cookie consent
 *
 * Detects user's country and determines if GDPR consent banner is required.
 * Falls back to showing banner if detection fails (fail-safe approach).
 *
 * Matches the marketing site (attain-ss-public) implementation.
 */

// EU/EEA countries that require GDPR consent
const GDPR_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'GB', // UK (post-Brexit, still GDPR equivalent)
  'IS', 'LI', 'NO', // EEA non-EU
];

/**
 * Check if a country requires GDPR consent.
 * Returns true for EU/UK, false for others.
 * Returns true (fail-safe) if country code is null/unknown.
 */
export function requiresGdprConsent(countryCode: string | null): boolean {
  if (!countryCode) return true; // Fail-safe: show banner if unknown
  return GDPR_COUNTRIES.includes(countryCode.toUpperCase());
}

/**
 * Detect user's country code using ipapi.co free API.
 *
 * Returns two-letter country code (e.g., 'US', 'GB', 'FR') or null if detection fails.
 * Free tier: 1000 requests/day (sufficient for launch traffic).
 */
export async function detectCountryCode(): Promise<string | null> {
  try {
    const response = await fetch('https://ipapi.co/country_code/', {
      method: 'GET',
      headers: { 'Accept': 'text/plain' },
    });

    if (response.ok) {
      const countryCode = await response.text();
      return countryCode.trim();
    }
  } catch (error) {
    console.warn('[Geo] Detection failed, defaulting to showing banner:', error);
  }

  return null; // Unknown = show banner (fail-safe)
}

/**
 * Determine if cookie consent banner should be shown to this user.
 *
 * Returns true for EU/UK users (GDPR required).
 * Returns false for US and other non-GDPR countries (auto-accept tracking).
 * Returns true if detection fails (fail-safe).
 */
export async function shouldShowConsentBanner(): Promise<boolean> {
  const countryCode = await detectCountryCode();
  return requiresGdprConsent(countryCode);
}
